import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Upload,
  App as AntApp,
} from "antd";
import type { RcFile } from "antd/es/upload";
import {
  DollarOutlined,
  EditOutlined,
  InboxOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
  UploadOutlined,
} from "@ant-design/icons";

import { catalogApi } from "../api/catalog";
import { paymentsApi } from "../api/payments";
import { usersApi } from "../api/users";
import type { Shop, User } from "../api/types";
import { PageHeader } from "../components/PageHeader";
import { creditUtilColor, creditUtilLabel, formatINR } from "../utils/format";

export function Shops() {
  const { notification } = AntApp.useApp();
  const [rows, setRows] = useState<Shop[]>([]);
  const [employees, setEmployees] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editShop, setEditShop] = useState<Shop | null>(null);
  const [payingShop, setPayingShop] = useState<Shop | null>(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [payForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await catalogApi.listShops({ search, page, page_size: pageSize });
      setRows(res.results);
      setTotal(res.count);
    } finally {
      setLoading(false);
    }
  }, [search, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  // Load active employees once for the assignment dropdowns. 500 covers any
  // realistic org size; if it grows we can swap this for server-side search.
  useEffect(() => {
    usersApi
      .list({ role: "employee", is_active: true, page_size: 500 })
      .then((res) => setEmployees(res.results))
      .catch(() => setEmployees([]));
  }, []);

  const employeeOptions = useMemo(
    () =>
      employees.map((e) => ({
        label: e.full_name,
        value: e.id,
        // `email` is also matched by the searchable select via optionFilterProp.
        email: e.email,
      })),
    [employees],
  );

  useEffect(() => {
    if (editShop) {
      editForm.setFieldsValue({
        name: editShop.name,
        address: editShop.address,
        area: editShop.area,
        city: editShop.city,
        gst_number: editShop.gst_number || "",
        phone: editShop.phone || "",
        pincode: editShop.pincode || "",
        district_id: editShop.district_id || "",
        credit_limit: Number(editShop.credit_limit ?? 0),
        outstanding_amount: Number(editShop.outstanding_amount ?? 0),
        opening_balance: Number(editShop.opening_balance ?? 0),
        closing_balance: Number(editShop.closing_balance ?? 0),
        allocated_employee_ids: editShop.allocated_employees.map((e) => e.id),
      });
    }
  }, [editShop, editForm]);

  const onPay = async () => {
    if (!payingShop) return;
    try {
      const vals = await payForm.validateFields();
      const res = await paymentsApi.create({
        shop: payingShop.id,
        amount: vals.amount,
        method: vals.method,
        reference: vals.reference || "",
        notes: vals.notes || "",
      });
      notification.success({
        message: "Payment recorded",
        description: `New outstanding for ${payingShop.name}: ${formatINR(res.outstanding_after)}`,
      });
      setPayingShop(null);
      payForm.resetFields();
      load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: Record<string, unknown> }; errorFields?: unknown };
      if (err.errorFields) return;
      const data = err.response?.data;
      const msg = (data && typeof data.detail === "string" ? data.detail : null) || "Could not record payment";
      notification.error({ message: msg });
    }
  };

  const onAdd = async () => {
    try {
      const vals = await form.validateFields();
      const payload = {
        ...vals,
        credit_limit: vals.credit_limit ?? 0,
        outstanding_amount: vals.outstanding_amount ?? 0,
        allocated_employee_ids: vals.allocated_employee_ids || [],
      };
      await catalogApi.createShop(payload);
      notification.success({ message: "Shop added" });
      setAddOpen(false);
      form.resetFields();
      load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: Record<string, string[] | string> } };
      if (err.response?.data) {
        const data = err.response.data;
        const msg = (typeof data.detail === "string" ? data.detail : null) || "Could not create shop";
        notification.error({ message: msg });
      }
    }
  };

  const onImport = async (file: RcFile) => {
    setImporting(true);
    try {
      const res = await catalogApi.bulkUploadShops(file);
      const bits = [`${res.created_rows} created`, `${res.updated_rows} updated`];
      if (res.assigned_rows > 0) bits.push(`${res.assigned_rows} assigned to employees`);
      if (res.gst_dropped > 0) bits.push(`${res.gst_dropped} invalid GST skipped`);
      if (res.failed_rows > 0) bits.push(`${res.failed_rows} errors`);
      notification.success({
        message: "Ledger imported",
        description: bits.join(" · "),
        duration: 6,
      });
      if (res.unknown_employee_codes?.length) {
        notification.warning({
          message: "Unrecognised employee codes — those shops were left unassigned",
          description: res.unknown_employee_codes.join(", "),
          duration: 12,
        });
      }
      if (res.errors?.length) {
        notification.warning({
          message: "Some rows were skipped",
          description: res.errors
            .slice(0, 3)
            .map((e) => `${e.name || `row ${e.row_number}`}: ${e.reason}`)
            .join("\n"),
          duration: 10,
        });
      }
      setImportOpen(false);
      setPage(1);
      load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      notification.error({ message: err.response?.data?.detail || "Import failed" });
    } finally {
      setImporting(false);
    }
    return false; // prevent antd's default upload
  };

  const onEdit = async () => {
    if (!editShop) return;
    try {
      const vals = await editForm.validateFields();
      const payload = {
        ...vals,
        credit_limit: vals.credit_limit ?? 0,
        outstanding_amount: vals.outstanding_amount ?? 0,
        allocated_employee_ids: vals.allocated_employee_ids || [],
      };
      await catalogApi.updateShop(editShop.id, payload);
      notification.success({ message: "Shop updated" });
      setEditShop(null);
      load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: Record<string, string[] | string> } };
      if (err.response?.data) {
        const data = err.response.data;
        const msg = (typeof data.detail === "string" ? data.detail : null) || "Could not update shop";
        notification.error({ message: msg });
      }
    }
  };

  return (
    <div className="page-shell">
      <PageHeader
        title="Shops"
        subtitle="Retailers and outlets that receive orders"
        right={
          <Space>
            <Button icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>
              Import Ledger
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
              Add Shop
            </Button>
          </Space>
        }
      />

      <div className="toolbar-row">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search name, area, city…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ maxWidth: 360 }}
        />
        <div style={{ marginLeft: "auto", fontSize: 13, color: "var(--text-secondary)" }}>
          {total} total
        </div>
      </div>

      <Table
        rowKey="id"
        size="middle"
        loading={loading}
        dataSource={rows}
        scroll={{ x: 1080 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        columns={[
          {
            title: "Shop",
            dataIndex: "name",
            width: 200,
            render: (v: string, row: Shop) => {
              const sub = [row.area, row.city, row.phone].filter(Boolean).join(" · ");
              return (
                <div>
                  <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>{v}</div>
                  {sub && <div className="small-muted">{sub}</div>}
                  {row.district_id && (
                    <Tag style={{ marginTop: 4, fontSize: 11 }}>District {row.district_id}</Tag>
                  )}
                </div>
              );
            },
          },
          {
            title: "Outstanding",
            dataIndex: "outstanding_amount",
            width: 120,
            align: "right" as const,
            render: (v: string) => (
              <span className="tabular" style={{ fontWeight: Number(v) > 0 ? 600 : 400 }}>
                {formatINR(v)}
              </span>
            ),
          },
          {
            title: "Opening Balance",
            dataIndex: "opening_balance",
            width: 150,
            align: "right" as const,
            render: (v: string) => (
              <span className="tabular">{formatINR(v)}</span>
            ),
          },
          {
            title: "Closing Balance",
            dataIndex: "closing_balance",
            width: 150,
            align: "right" as const,
            render: (v: string) => (
              <span className="tabular" style={{ fontWeight: Number(v) > 0 ? 600 : 400 }}>
                {formatINR(v)}
              </span>
            ),
          },
          {
            title: "Credit",
            key: "credit",
            width: 210,
            render: (_: unknown, row: Shop) => {
              const limit = Number(row.credit_limit);
              if (limit <= 0) return <span className="small-muted">No limit set</span>;
              const avail = Number(row.available_credit);
              const pct = Number(row.credit_utilization);
              const color = creditUtilColor(pct);
              return (
                <div style={{ minWidth: 160 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <span
                      className="tabular"
                      style={{
                        fontWeight: 600,
                        fontSize: 13,
                        color: avail < 0 ? "var(--danger)" : "var(--text-primary)",
                      }}
                    >
                      {formatINR(avail)}
                    </span>
                    <span className="small-muted">of {formatINR(limit)}</span>
                  </div>
                  <Tooltip title={`${creditUtilLabel(pct)} · ${formatINR(row.outstanding_amount)} outstanding`}>
                    <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8 }}>
                      <div
                        style={{
                          flex: 1,
                          height: 5,
                          background: "var(--bg-hover)",
                          borderRadius: 999,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${Math.min(pct, 100)}%`,
                            height: "100%",
                            background: color,
                            borderRadius: 999,
                          }}
                        />
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 600, color, minWidth: 30, textAlign: "right" }}>
                        {pct.toFixed(0)}%
                      </span>
                    </div>
                  </Tooltip>
                </div>
              );
            },
          },
          {
            title: "Employees",
            dataIndex: "allocated_employees",
            width: 160,
            render: (emps: Shop["allocated_employees"]) =>
              emps && emps.length > 0 ? (
                <Space size={[4, 4]} wrap>
                  {emps.slice(0, 2).map((e) => (
                    <Tag key={e.id} style={{ margin: 0 }}>{e.full_name}</Tag>
                  ))}
                  {emps.length > 2 && <Tag style={{ margin: 0 }}>+{emps.length - 2}</Tag>}
                </Space>
              ) : (
                <span className="small-muted">—</span>
              ),
          },
          {
            title: "",
            key: "_actions",
            width: 56,
            fixed: "right" as const,
            align: "center" as const,
            render: (_: unknown, row: Shop) => (
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: [
                    {
                      key: "pay",
                      icon: <DollarOutlined />,
                      label: "Record Payment",
                      onClick: () => setPayingShop(row),
                    },
                    {
                      key: "edit",
                      icon: <EditOutlined />,
                      label: "Edit shop",
                      onClick: () => setEditShop(row),
                    },
                  ],
                }}
              >
                <Button type="text" icon={<MoreOutlined />} />
              </Dropdown>
            ),
          },
        ]}
      />

      <Modal
        title="Import Shops from Ledger"
        open={importOpen}
        onCancel={() => setImportOpen(false)}
        footer={null}
      >
        <div style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 10 }}>
          Accepts a Tally <strong>"List of Ledgers"</strong> export (<code>.xlsx</code>, <code>.csv</code>{" "}
          or <code>.pdf</code>). Columns are auto-detected — <em>Name of Ledger</em>, <em>Employee</em>{" "}
          (emp code, e.g. EMP00007), <em>GSTIN</em>, <em>Contact No</em>, <em>Address</em>,{" "}
          <em>Pincode</em> and <em>District ID</em>. The employee code assigns the shop to that
          salesman; title rows and totals are ignored. Shops are matched by name, so re-uploading
          updates existing shops instead of duplicating them.
        </div>
        <Upload.Dragger
          name="file"
          accept=".xlsx,.xlsm,.csv,.pdf"
          multiple={false}
          disabled={importing}
          beforeUpload={onImport}
          showUploadList={false}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            {importing ? "Importing…" : "Drop ledger file here or click to browse"}
          </p>
          <p className="ant-upload-hint" style={{ fontSize: 12 }}>
            Excel (xlsx) · CSV · PDF (text-based, not scanned images)
          </p>
        </Upload.Dragger>
      </Modal>

      <Modal title="Add Shop" open={addOpen} onCancel={() => setAddOpen(false)} onOk={onAdd} okText="Create">
        <Form
          layout="vertical"
          form={form}
          requiredMark={false}
          initialValues={{ credit_limit: 0, outstanding_amount: 0, opening_balance: 0, closing_balance: 0 }}
        >
          <Form.Item label="Name" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Address" name="address" rules={[{ required: true }]}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Space style={{ width: "100%" }}>
            <Form.Item label="Area" name="area" style={{ flex: 1 }}>
              <Input />
            </Form.Item>
            <Form.Item label="City" name="city" style={{ flex: 1 }}>
              <Input />
            </Form.Item>
          </Space>
          <Space style={{ width: "100%" }}>
            <Form.Item label="Phone" name="phone" style={{ flex: 1 }}>
              <Input maxLength={32} placeholder="Contact number" />
            </Form.Item>
            <Form.Item label="Pincode" name="pincode" style={{ flex: 1 }}>
              <Input maxLength={10} placeholder="626001" />
            </Form.Item>
          </Space>
          <Form.Item label="District ID" name="district_id" extra="Optional. Populated automatically from ledger imports when present.">
            <Input maxLength={64} placeholder="e.g. DIST-05" />
          </Form.Item>
          <Space style={{ width: "100%" }} align="start">
            <Form.Item
              label="Credit Limit (₹)"
              name="credit_limit"
              style={{ flex: 1 }}
              extra="Max credit allowed. 0 = no limit."
            >
              <InputNumber min={0} step={1000} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              label="Outstanding (₹)"
              name="outstanding_amount"
              style={{ flex: 1 }}
              extra="Amount currently owed."
            >
              <InputNumber min={0} step={500} style={{ width: "100%" }} />
            </Form.Item>
          </Space>
          <Form.Item
            label="Assign to Employees"
            name="allocated_employee_ids"
            extra="Only the selected employees will see this shop in their app."
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder={employees.length === 0 ? "No active employees" : "Search and select employees"}
              optionFilterProp="label"
              filterOption={(input, option) => {
                const q = input.toLowerCase();
                return (
                  String(option?.label || "").toLowerCase().includes(q) ||
                  String((option as { email?: string })?.email || "").toLowerCase().includes(q)
                );
              }}
              options={employeeOptions}
            />
          </Form.Item>
          <Space style={{ width: "100%" }} align="start">
            <Form.Item
              label="Opening Balance (₹)"
              name="opening_balance"
              style={{ flex: 1 }}
              extra="Previous year's closing balance. Stays fixed."
              initialValue={0}
            >
              <InputNumber min={0} step={1000} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              label="Closing Balance (₹)"
              name="closing_balance"
              style={{ flex: 1 }}
              extra="Current balance. Updates with bills and repayments."
              initialValue={0}
            >
              <InputNumber min={0} step={500} style={{ width: "100%" }} />
            </Form.Item>
          </Space>
        </Form>
      </Modal>

      <Modal
        title={editShop ? `Edit ${editShop.name}` : "Edit Shop"}
        open={!!editShop}
        onCancel={() => setEditShop(null)}
        onOk={onEdit}
        okText="Save"
        destroyOnClose
      >
        <Form layout="vertical" form={editForm} requiredMark={false} preserve={false}>
          <Form.Item label="Name" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Address" name="address" rules={[{ required: true }]}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Space style={{ width: "100%" }}>
            <Form.Item label="Area" name="area" style={{ flex: 1 }}>
              <Input />
            </Form.Item>
            <Form.Item label="City" name="city" style={{ flex: 1 }}>
              <Input />
            </Form.Item>
          </Space>
          <Space style={{ width: "100%" }}>
            <Form.Item label="Phone" name="phone" style={{ flex: 1 }}>
              <Input maxLength={32} placeholder="Contact number" />
            </Form.Item>
            <Form.Item label="Pincode" name="pincode" style={{ flex: 1 }}>
              <Input maxLength={10} placeholder="626001" />
            </Form.Item>
          </Space>
          <Form.Item label="District ID" name="district_id" extra="Optional. Populated automatically from ledger imports when present.">
            <Input maxLength={64} placeholder="e.g. DIST-05" />
          </Form.Item>
          <Space style={{ width: "100%" }} align="start">
            <Form.Item
              label="Credit Limit (₹)"
              name="credit_limit"
              style={{ flex: 1 }}
              extra="Max credit allowed. 0 = no limit."
            >
              <InputNumber min={0} step={1000} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              label="Outstanding (₹)"
              name="outstanding_amount"
              style={{ flex: 1 }}
              extra="Adjusts automatically on bills & payments."
            >
              <InputNumber min={0} step={500} style={{ width: "100%" }} />
            </Form.Item>
          </Space>
          <Space style={{ width: "100%" }} align="start">
            <Form.Item
              label="Opening Balance (₹)"
              name="opening_balance"
              style={{ flex: 1 }}
              extra="Previous year's closing balance. Stays fixed."
            >
              <InputNumber min={0} step={1000} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              label="Closing Balance (₹)"
              name="closing_balance"
              style={{ flex: 1 }}
              extra="Current balance. Updates with bills and repayments."
            >
              <InputNumber min={0} step={500} style={{ width: "100%" }} />
            </Form.Item>
          </Space>
          <Form.Item
            label="Assigned Employees"
            name="allocated_employee_ids"
            extra="Only the selected employees will see this shop in their app."
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder={employees.length === 0 ? "No active employees" : "Search and select employees"}
              optionFilterProp="label"
              filterOption={(input, option) => {
                const q = input.toLowerCase();
                return (
                  String(option?.label || "").toLowerCase().includes(q) ||
                  String((option as { email?: string })?.email || "").toLowerCase().includes(q)
                );
              }}
              options={employeeOptions}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={payingShop ? `Record Payment — ${payingShop.name}` : "Record Payment"}
        open={!!payingShop}
        onCancel={() => setPayingShop(null)}
        onOk={onPay}
        okText="Record Payment"
        destroyOnClose
      >
        {payingShop && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 8,
              marginBottom: 16,
              fontSize: 13,
            }}
          >
            <div>
              <div className="small-muted">Credit Limit</div>
              <div className="tabular">{formatINR(payingShop.credit_limit)}</div>
            </div>
            <div>
              <div className="small-muted">Outstanding</div>
              <div className="tabular">{formatINR(payingShop.outstanding_amount)}</div>
            </div>
            <div>
              <div className="small-muted">Available</div>
              <div className="tabular">{formatINR(payingShop.available_credit)}</div>
            </div>
          </div>
        )}
        <Form
          layout="vertical"
          form={payForm}
          requiredMark={false}
          preserve={false}
          initialValues={{ method: "cash" }}
        >
          <Form.Item label="Amount (₹)" name="amount" rules={[{ required: true, message: "Enter an amount" }]}>
            <InputNumber min={0.01} step={500} style={{ width: "100%" }} placeholder="e.g. 5000" />
          </Form.Item>
          <Form.Item label="Method" name="method">
            <Select
              options={[
                { label: "Cash", value: "cash" },
                { label: "UPI", value: "upi" },
                { label: "Cheque", value: "cheque" },
                { label: "Bank Transfer", value: "bank_transfer" },
                { label: "Other", value: "other" },
              ]}
            />
          </Form.Item>
          <Form.Item label="Reference" name="reference" extra="Optional — cheque no., UPI ref, etc.">
            <Input maxLength={128} />
          </Form.Item>
          <Form.Item label="Notes" name="notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
